import React, { useEffect, useState, useRef, useCallback } from "react";
import "./project.css";
import { Link, useLoaderData, useSearchParams, useNavigate } from "react-router-dom";
import LoaderTable from "../../../../Components/pageLoader/LoaderTable";
import LoaderSpinner from "../../../../Components/pageLoader/LoaderSpinner";
import axios from "axios";
import ModalCreateProject from "../../../../Components/Modal/ModalInventory/ModalCreateProject";
import Swal from "sweetalert2";

// Moved outside to persist during component lifecycle if needed, 
// or kept inside if you want it cleared on unmount.
const ordersCache = new Map();

const ProjectTest = () => {
  const { products, totalPages } = useLoaderData();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // States
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [dateFilter, setDateFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [phoneNumberFilter, setPhoneNumberFilter] = useState("");
  const [EmailFilter, setEmailFilter] = useState("");
  const [NameFilter, setNameFilter] = useState("");
  const [filterType, setFilterType] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [isFiltersCleared, setIsFiltersCleared] = useState(true);
  
  const [realTimeData, setRealTimeData] = useState(() => products || []);
  const [loading, setLoading] = useState(false);
  const [isDropdownCreateProject, setIsDropdownCreateProject] = useState(false);
  const [readRows, setReadRows] = useState({});

  const abortControllerRef = useRef(null);

  // ================= FETCH LOGIC (SPEED OPTIMIZED) =================
  const fetchNewData = useCallback(async () => {
    const params = {
      page: searchParams.get("page") || 1,
      dateFilter: searchParams.get("dateFilter") || "",
      fromDate: searchParams.get("fromDate") || "",
      toDate: searchParams.get("toDate") || "",
      phoneNumber: searchParams.get("phoneNumber") || "",
      email: searchParams.get("email") || "",
      position: searchParams.get("position") || "",
      status: searchParams.get("status") || "",
      emailStatus: searchParams.get("emailStatus") || "",
      name: searchParams.get("name") || "",
      filterType: searchParams.get("filterType") || "",
    };

    const cacheKey = JSON.stringify(params);

    // 1. Instant Cache Return
    if (ordersCache.has(cacheKey)) {
      setRealTimeData(ordersCache.get(cacheKey));
      return; 
    }

    // 2. Abort previous pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();

    try {
      setLoading(true);
      const queryString = new URLSearchParams({ ...params, limit: 20 }).toString();
      
      const res = await fetch(`https://server-gs.vercel.app/admin/orders?${queryString}`, {
        signal: abortControllerRef.current.signal
      });

      const data = await res.json();

      // 3. Update Cache and State
      ordersCache.set(cacheKey, data.products);
      setRealTimeData(data.products);
    } catch (err) {
      if (err.name !== 'AbortError') {
        console.error("Fetch error:", err);
      }
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchNewData();
  }, [fetchNewData]);

  // Sync initial read state from data
  useEffect(() => {
    if (realTimeData?.length) {
      const initialReadState = realTimeData.reduce((acc, product) => {
        acc[product._id] = product.view === true;
        return acc;
      }, {});
      setReadRows(initialReadState);
    }
  }, [realTimeData]);

  // Update filter cleared state
  useEffect(() => {
    const hasFilters = 
      searchParams.get("dateFilter") ||
      searchParams.get("fromDate") ||
      searchParams.get("toDate") ||
      searchParams.get("phoneNumber") ||
      searchParams.get("email") ||
      searchParams.get("name") ||
      searchParams.get("filterType") ||
      searchParams.get("status") ||
      searchParams.get("emailStatus");
    
    setIsFiltersCleared(!hasFilters);
    
    // Sync local state with URL params
    setDateFilter(searchParams.get("dateFilter") || "");
    setFromDate(searchParams.get("fromDate") || "");
    setToDate(searchParams.get("toDate") || "");
    setPhoneNumberFilter(searchParams.get("phoneNumber") || "");
    setEmailFilter(searchParams.get("email") || "");
    setNameFilter(searchParams.get("name") || "");
    setFilterType(searchParams.get("filterType") || "");
    
    // Set status filter based on URL
    const status = searchParams.get("status");
    const emailStatus = searchParams.get("emailStatus");
    if (emailStatus === "sent") {
      setStatusFilter("sent");
    } else if (status) {
      setStatusFilter(status);
    } else {
      setStatusFilter("");
    }
  }, [searchParams]);

  // ================= HANDLERS =================
  
  const handleRowClick = async (id) => {
    if (readRows[id]) return;
    setReadRows(prev => ({ ...prev, [id]: true }));
    try {
      await fetch("https://server-gs.vercel.app/order-view", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ orderId: id, view: true }),
      });
    } catch (error) {
      setReadRows(prev => ({ ...prev, [id]: false }));
    }
  };

const handleDuplicate = async (productId) => {
const result = await Swal.fire({
  title: "Duplicate",
  html: `
    <div class="text-center">
      <div class="text-gray-600 mb-4">
        Create a copy of this project?
      </div>
      <div class="text-sm text-gray-500">
        All details will be duplicated.
      </div>
    </div>
  `,
  showCancelButton: true,
  confirmButtonText: "Duplicate",
  cancelButtonText: "Cancel",
  confirmButtonColor: "#2563eb",
  cancelButtonColor: "#9ca3af",
  background: "#ffffff",
  color: "#1f2937",
  width: "380px"
});


  if (!result.isConfirmed) return;

  try {
    const response = await axios.post("https://server-gs.vercel.app/duplicate-product", { productId });
    if (response.status === 200) {
   Swal.fire({
  title: "Duplicated!",
  icon: "success",
  timer: 800,
  showConfirmButton: false,
    background: "fff",
  color: "#000",
  customClass: {
    popup: "swal-bw",
    title: "swal-bw-title",
    icon: "swal-bw-icon",
  }
});

      
      // Clear cache to force fresh fetch
      ordersCache.clear();
      
      // Force immediate refresh with a slight delay
      setTimeout(() => {
        fetchNewData();
      }, 100);
    }
  } catch (error) {
    Swal.fire("Error", "Failed to duplicate.", "error");
  }
};

  const handleDelete = async (orderId) => {
  const result = await Swal.fire({
  title: "Delete Project",
  html: `
    <div class="text-center">
      <div class="text-gray-600 mb-4">
        This action cannot be undone. All project data will be permanently removed.
      </div>
      <div class="text-sm text-gray-500">
        This includes client details, invoices, and all associated files.
      </div>
    </div>
  `,
  showCancelButton: true,
  confirmButtonText: "Delete",
  cancelButtonText: "Cancel",
  confirmButtonColor: "#dc2626",
  cancelButtonColor: "#6b7280",
  background: "#ffffff",
  color: "#1f2937",
  width: "400px",
  icon: "warning",
  iconColor: "#ef4444"
});

  if (!result.isConfirmed) return;

  try {
    const response = await fetch("https://server-gs.vercel.app/delete-order", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ orderId }),
    });

    if (response.ok) {
      setRealTimeData(prev => prev.filter(item => item._id !== orderId));

      Swal.fire({
        title: "Deleted!",
        icon: "success",
        timer: 800,
        showConfirmButton: false,
        background: "#fff",
        color: "#000",
        customClass: {
          popup: "swal-wb",
          title: "swal-wb-title",
          icon: "swal-wb-icon"
        }
      });
            ordersCache.clear();
      
      // Force immediate refresh with a slight delay
      setTimeout(() => {
        fetchNewData();
      }, 100);
    }
  } catch (error) {
    Swal.fire({
      title: "Error",
      text: "An error occurred",
      icon: "error",
      background: "#fff",
      color: "#000",
      customClass: {
        popup: "swal-wb",
        title: "swal-wb-title",
        icon: "swal-wb-icon"
      }
    });
  }
};

  const handleApplyFilters = () => {
    const newParams = new URLSearchParams();
    if (dateFilter) newParams.set("dateFilter", dateFilter);
    if (fromDate) newParams.set("fromDate", fromDate);
    if (toDate) newParams.set("toDate", toDate);
    if (phoneNumberFilter) newParams.set("phoneNumber", phoneNumberFilter);
    if (EmailFilter) newParams.set("email", EmailFilter);
    if (NameFilter) newParams.set("name", NameFilter);
    if (filterType) newParams.set("filterType", filterType);
    if (statusFilter && statusFilter !== "all") {
      if (statusFilter === "sent") {
        newParams.set("emailStatus", "sent");
      } else {
        newParams.set("status", statusFilter);
      }
    }
    newParams.set("page", "1");
    setSearchParams(newParams);
    setIsFilterOpen(false);
  };

  const handleCancelFilters = () => {
    setDateFilter("");
    setFromDate("");
    setToDate("");
    setPhoneNumberFilter("");
    setEmailFilter("");
    setNameFilter("");
    setFilterType("");
    setStatusFilter("");
    setSearchParams({ page: "1" });
    setIsFiltersCleared(true);
  };

  const handleCancelStatus = () => {
    setSearchParams(prevParams => {
      prevParams.delete("emailStatus");
      prevParams.delete("status");
      prevParams.set("page", "1");
      return prevParams;
    });
    setStatusFilter("");
    setIsFiltersCleared(true);
  };

  const handleTabChangeStatus = (status) => {
    setStatusFilter(status);
    setSearchParams((prevParams) => {
      prevParams.delete("emailStatus");
      prevParams.delete("status");
      if (status === "sent") {
        prevParams.set("emailStatus", "sent");
      } else if (status !== "all") {
        prevParams.set("status", status);
      }
      prevParams.set("page", "1");
      return prevParams;
    });
  };

  const toggleFilter = () => {
    setIsFilterOpen(!isFilterOpen);
  };

  const openModalCreateProject = () => {
    setIsDropdownCreateProject(true);
  };

  const closeModalCreateProject = () => {
    setIsDropdownCreateProject(false);
  };

  const handlePreviousPage = () => {
    const currentPage = parseInt(searchParams.get("page")) || 1;
    if (currentPage > 1) {
      setSearchParams(prev => {
        prev.set("page", (currentPage - 1).toString());
        return prev;
      });
    }
  };

  const handleNextPage = () => {
    const currentPage = parseInt(searchParams.get("page")) || 1;
    if (currentPage < totalPages) {
      setSearchParams(prev => {
        prev.set("page", (currentPage + 1).toString());
        return prev;
      });
    }
  };

  // Add a function to handle new project creation
const handleNewProjectCreated = () => {
  // Clear cache to ensure fresh data
  ordersCache.clear();
  
  // Force refresh
  fetchNewData();
  
  // Optional: Navigate to first page
  setSearchParams(prev => {
    const newParams = new URLSearchParams(prev);
    newParams.set("page", "1");
    return newParams;
  });
};

  const formatPositionStatus = (status) => {
    return status.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/^./, str => str.toUpperCase());
  };

  // Color Helpers
  const getStatusColor = (status, isActive) => {
    const colors = {
      draft: isActive ? "from-gray-700 to-gray-600" : "from-gray-400 to-gray-300",
      sent: isActive ? "from-blue-700 to-blue-600" : "from-blue-400 to-blue-300",
      partial: isActive ? "from-yellow-600 to-yellow-500" : "from-yellow-400 to-yellow-300",
      paid: isActive ? "from-green-700 to-green-600" : "from-green-400 to-green-300",
      all: isActive ? "bg-white text-blue-600" : "text-gray-500"
    };
    return colors[status] || "from-blue-500 to-blue-400";
  };

  const getActiveStatusColor = (status) => {
    const colors = {
      draft: "bg-gradient-to-r from-gray-700 to-gray-600",
      sent: "bg-gradient-to-r from-blue-700 to-blue-600",
      partial: "bg-gradient-to-r from-yellow-600 to-yellow-500",
      paid: "bg-gradient-to-r from-green-700 to-green-600",
    };
    return colors[status] || "bg-gradient-to-r from-blue-600 to-blue-500";
  };

  const currentPage = parseInt(searchParams.get("page")) || 1;

  return (
    <div>
      {loading ? (
        <div className="flex h-screen items-center justify-center">
          <LoaderSpinner />
        </div>
      ) : (
        <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
          {/* ================= HEADER ================= */}
          <header className="w-full h-20 flex-shrink-0 bg-white border-b border-gray-200 flex justify-between items-center px-6 sticky top-0 z-40 shadow-sm">
            <div className="flex items-center gap-4">
              <button
                onClick={toggleFilter}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 hover:border-blue-400 transition-all duration-200 shadow-sm"
              >
                {isFilterOpen ? (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h18v2l-7 7v5l-4 4v-9L3 6V4z" />
                  </svg>
                )}
                <span>{isFilterOpen ? "Close Filters" : "Filter Projects"}</span>
              </button>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Projects</h1>
            </div>

            {/* Status Navigation Tabs using your custom color functions */}
            <div className="md:flex bg-gray-100 p-1.5 rounded-xl border border-gray-200 gap-1.5">
              <button
                onClick={handleCancelStatus}
                className={`px-5 py-1.5 rounded-lg text-sm font-bold transition-all duration-300 ${
                  isFiltersCleared
                    ? "bg-white text-blue-600 shadow-md ring-1 ring-black/5 scale-105 z-10"
                    : "text-gray-500 hover:text-gray-700 hover:bg-gray-200/50"
                }`}
              >
                All
              </button>

              {["draft", "sent", "partial", "paid"].map((status) => {
                const isActive = statusFilter === status;
                return (
                  <button
                    key={status}
                    onClick={() => handleTabChangeStatus(status)}
                    className={`px-5 py-1.5 rounded-lg text-sm font-bold capitalize transition-all duration-300 shadow-sm ${
                      isActive
                        ? `${getActiveStatusColor(status)} text-white shadow-md transform scale-105 z-10`
                        : `text-gray-500 hover:text-gray-800 hover:bg-gray-200/60`
                    }`}
                  >
                    {formatPositionStatus(status)}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-3">
              <button
                onClick={openModalCreateProject}
                className="px-5 py-2.5 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 active:scale-95 focus:ring-4 focus:ring-blue-200 transition-all shadow-lg shadow-blue-100 flex items-center"
              >
                <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                </svg>
                Create Project
              </button>
            </div>
          </header>

          {/* ================= MAIN BROWSER AREA ================= */}
          <div className="flex flex-1 overflow-hidden relative">
            {/* Sidebar - Perfectly Transitioned */}
            <aside
              className={`bg-white border-r border-gray-200 transition-all duration-300 ease-in-out overflow-hidden flex-shrink-0 ${
                isFilterOpen ? "w-[320px] opacity-100" : "w-0 opacity-0 border-none"
              }`}
            >
              <div className="h-[calc(100vh-80px)] overflow-y-auto p-6 space-y-6 shadow-inner">
                {/* Header Section */}
                <div className="flex items-center justify-between mb-2">
                  <h2 className="text-sm font-bold text-gray-400 uppercase tracking-widest">
                    Filter Options
                  </h2>
                  <button
                    onClick={handleCancelFilters}
                    className="text-xs text-blue-600 hover:underline font-medium"
                  >
                    Reset All
                  </button>
                </div>

                {/* Date Section */}
                <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                  <label className="text-sm font-semibold text-gray-700 block mb-2">
                    Logistics Start Date
                  </label>
                  <select
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    className="w-full border-gray-300 rounded-lg p-2.5 text-sm focus:ring-blue-500 focus:border-blue-500 bg-white"
                  >
                    <option value="">Select Dates</option>
                    <option value="previous90days">Previous 90 Days</option>
                    <option value="previous30days">Previous 30 Days</option>
                    <option value="today">Today</option>
                    <option value="tomorrow">Tomorrow</option>
                    <option value="next30days">Next 30 Days</option>
                    <option value="next90days">Next 90 Days</option>
                  </select>

                  <div className="grid grid-cols-2 gap-2 mt-3">
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 uppercase font-bold ml-1 mb-1">From</span>
                      <input
                        type="date"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        className="w-full border-gray-300 rounded-lg p-2 text-xs focus:ring-blue-500"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[10px] text-gray-400 uppercase font-bold ml-1 mb-1">To</span>
                      <input
                        type="date"
                        value={toDate}
                        min={fromDate}
                        onChange={(e) => setToDate(e.target.value)}
                        className="w-full border-gray-300 rounded-lg p-2 text-xs focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Identity Section */}
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-semibold text-gray-700 block mb-1">Client Details</label>
                    <input
                      type="text"
                      placeholder="Full Name..."
                      value={NameFilter}
                      onChange={(e) => setNameFilter(e.target.value)}
                      className="w-full border-gray-300 rounded-lg p-2.5 text-sm mb-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Phone Number..."
                      value={phoneNumberFilter}
                      onChange={(e) => setPhoneNumberFilter(e.target.value)}
                      className="w-full border-gray-300 rounded-lg p-2.5 text-sm mb-2 focus:ring-blue-500"
                    />
                    <input
                      type="text"
                      placeholder="Email Address..."
                      value={EmailFilter}
                      onChange={(e) => setEmailFilter(e.target.value)}
                      className="w-full border-gray-300 rounded-lg p-2.5 text-sm focus:ring-blue-500"
                    />
                  </div>
                </div>

                {/* Project Timeline Toggle */}
                <div className="space-y-3">
                  <label className="text-sm font-semibold text-gray-700 block">Project Status</label>
                  <div className="flex p-1 bg-gray-100 rounded-lg">
                    <button
                      onClick={() => setFilterType("upcomingProjects")}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                        filterType === "upcomingProjects"
                          ? "bg-white shadow text-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Upcoming
                    </button>
                    <button
                      onClick={() => setFilterType("pastProjects")}
                      className={`flex-1 py-1.5 text-xs font-medium rounded-md transition-all ${
                        filterType === "pastProjects"
                          ? "bg-white shadow text-blue-600"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      Past
                    </button>
                  </div>
                </div>

                {/* Action Button */}
                <button
                  onClick={handleApplyFilters}
                  className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-xl shadow-lg shadow-blue-100 transition-all transform active:scale-95 mt-4"
                >
                  Apply Filters
                </button>
              </div>
            </aside>

            {/* Table Content - Perfectly Transitioned */}
            <main className="flex-1 overflow-hidden flex flex-col bg-white">
              <div className="flex-1 overflow-auto">
                <table className="w-full text-sm text-left border-separate border-spacing-0">
                  <thead className="sticky top-0 z-20">
                    <tr className="bg-gray-50">
                      <th className="px-6 py-4 font-semibold text-gray-600 border-b border-gray-200 text-[13px] uppercase tracking-wider">Client Info</th>
                      <th className="px-6 py-4 font-semibold text-gray-600 border-b border-gray-200 text-[13px] uppercase tracking-wider">Contact</th>
                      <th className="px-6 py-4 font-semibold text-gray-600 border-b border-gray-200 text-[13px] uppercase tracking-wider">Logistics Start</th>
                      <th className="px-6 py-4 font-semibold text-gray-600 border-b border-gray-200 text-[13px] uppercase tracking-wider">Status</th>
                      <th className="px-6 py-4 font-semibold text-gray-600 border-b border-gray-200 text-[13px] uppercase tracking-wider">Financials</th>
                      <th className="px-6 py-4 font-semibold text-gray-600 border-b border-gray-200 text-center text-[13px] uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {realTimeData.map((product) => {
                      const isRead = readRows[product._id];
                      
                      return (
                        <tr 
                          key={product._id} 
                          className={`group transition-all duration-200 ${
                            isRead
                              ? "bg-white hover:bg-blue-50/40" 
                              : "bg-gray-100/80 font-semibold border-l-4 border-l-blue-500 shadow-sm"
                          }`}
                        >
                          {/* Client Info */}
                          <td className="px-6 py-4">
                            <Link 
                              to={`/dashboard/admin-project-details/${product._id}`} 
                              onClick={() => handleRowClick(product._id)}
                              className="block"
                            >
                              <h3 className={`text-sm transition-colors ${
                                isRead ? "text-gray-900 group-hover:text-blue-600 font-medium" : "text-blue-900 font-bold"
                              }`}>
                                {product.firstName} {product.lastName}
                              </h3>
                              <span className="text-[11px] text-gray-400 font-normal">
                                Created: {new Date(product.createdAt).toLocaleDateString()}
                              </span>
                            </Link>
                          </td>

                          {/* Contact */}
                          <td className={`px-6 py-4 text-md ${isRead ? "text-gray-600 font-normal" : "text-gray-800 font-bold"}`}>
                            <div className="flex flex-col">
                              <span>{product.phoneNumber}</span>
                              <span className="text-gray-400 text-md font-normal ">{product.email}</span>
                            </div>
                          </td>

                          {/* Logistics */}
                          <td className={`px-6 py-4 text-xs ${isRead ? "text-gray-500" : "text-gray-800"}`}>
                            {product.set?.[0]?.startDate ? (
                              <div className="flex flex-col">
                                <span>{new Date(product.set[0].startDate).toLocaleDateString()}</span>
                                <span className="text-[10px] text-gray-400 uppercase font-normal">
                                  {new Date(product.set[0].startDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </span>
                              </div>
                            ) : 'TBD'}
                          </td>

                          {/* Status */}
                          <td className="px-6 py-5">
                            <div className="relative group/status">
                              <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase border shadow-sm ${
                                product.status === 'paid' ? 'bg-emerald-50 text-emerald-700 border-emerald-100' : 
                                product.status === 'partial' ? 'bg-amber-50 text-amber-700 border-amber-100' :
                                'bg-blue-50 text-blue-700 border-blue-100'
                              }`}>
                                {product.status}
                              </span>
                              {/* Tooltip/Dropdown */}
                              <div className="absolute left-0 bottom-full mb-3 w-56 opacity-0 invisible group-hover/status:opacity-100 group-hover/status:visible transition-all duration-300 transform translate-y-2 group-hover/status:translate-y-0 z-40 pointer-events-none">
                                <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 p-4 ring-1 ring-black/5">
                                  <h4 className="text-xs font-bold text-gray-400 uppercase mb-3 border-b pb-2">
                                    Financial Summary
                                  </h4>
                                  <div className="space-y-3">
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-500">Paid</span>
                                      <span className="font-bold text-green-600">
                                        ${product.amountPaid || "0.00"}
                                      </span>
                                    </div>
                                    <div className="flex justify-between text-sm">
                                      <span className="text-gray-500">Due</span>
                                      <span className="font-bold text-red-500">
                                        ${product.amountDue || "0.00"}
                                      </span>
                                    </div>
                                    <div className="pt-2 border-t flex justify-between">
                                      <span className="text-sm font-bold">Total</span>
                                      <span className="text-sm font-black text-blue-600">
                                        ${product.totalAmount || "0.00"}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>

                          {/* Financials */}
                          <td className="px-6 py-4">
                            <div className={`text-xs ${isRead ? "text-gray-700 font-medium" : "text-gray-900 font-bold"}`}>
                              ${product.amountDue || "0.00"}
                            </div>
                            <div className="text-[10px] text-green-600 font-normal">Paid: ${product.amountPaid || "0.00"}</div>
                          </td>

                          {/* Actions */}
                          <td className="px-6 py-4">
                            <div className="flex justify-center gap-2">
                              <button 
                                onClick={() => handleDuplicate(product._id)} 
                                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-white rounded-full transition-all"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2" />
                                </svg>
                              </button>
                              <button 
                                onClick={() => handleDelete(product._id)} 
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-white rounded-full transition-all"
                              >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Sticky Pagination */}
              <footer className="flex-shrink-0 bg-white/95 backdrop-blur-sm border-t border-gray-200 px-6 py-4 flex items-center justify-between z-30">
                <span className="text-sm text-gray-500">
                  Page <span className="font-bold text-gray-800">{currentPage}</span> of {totalPages}
                </span>
                <div className="flex gap-2">
                  <button onClick={handlePreviousPage} disabled={currentPage === 1} className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50 transition-colors">Previous</button>
                  <button onClick={handleNextPage} disabled={currentPage === totalPages} className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 disabled:opacity-50 transition-transform active:scale-95">Next Page</button>
                </div>
              </footer>
            </main>
          </div>
        </div>
      )}

      <ModalCreateProject
        isVisible={isDropdownCreateProject}
        onClose={closeModalCreateProject}
        closeModal={() => setIsDropdownCreateProject(false)}
        onSuccess={handleNewProjectCreated} // Add this prop

      />
    </div>
  );
};

export default ProjectTest;